import React from 'react';
import { useNavigate } from 'react-router-dom';
import { EntryLayout } from '../../components/EntryLayout';
import { HabitItem } from '../../components/HabitItem';
import { MoodSelector } from '../../components/MoodSelector';
import { Button } from '../../components/ui/Button';
import { useHydration } from '../../contexts/HydrationContext';
import { useLocale } from '../../contexts/LocaleContext';
import { useProgress } from '../../contexts/ProgressContext';
import { formatLiters } from '../../utils/hydration';

/**
 * MOB/Onboarding/FirstCheck — the first Daily Check in under a minute.
 * One tap per habit, one tap for mood. Uses the same progression engine.
 */
export function OnboardingFirstCheck() {
  const {
    t,
    locale
  } = useLocale();
  const navigate = useNavigate();
  const progress = useProgress();
  const hydration = useHydration();
  const quickHabits = progress.habits.filter(habit => habit.id !== 'mood');
  return <EntryLayout title={t('eng.first.title')} body={t('eng.first.body')} footer={<div className="space-y-2" data-id="element-3379" data-mp="40s415xzdygh">
          <Button variant="gold" size="lg" fullWidth onClick={() => navigate('/onboarding/pronto')} data-id="element-3380" data-mp="3w42hil467k7a">
            <span className="bee-num" data-id="element-3381" data-mp="dyg2a5uspo3e">{t('eng.first.progress', {
          n: progress.dailyXp
        })}</span>
            <span aria-hidden="true" data-id="element-3382" data-mp="8zcuwercf5s9">·</span>
            {t('eng.first.continue')}
          </Button>
          <Button variant="quiet" fullWidth onClick={() => navigate('/onboarding/pronto')} data-id="element-3383" data-mp="mz4gb0k6voga">
            {t('eng.first.skip')}
          </Button>
        </div>} data-id="element-3378" data-mp="3v7i03zl1x8c7">
      <ul className="space-y-1" data-id="element-3384" data-mp="ixaj5297d2kv">
        {quickHabits.map(habit => habit.id === 'hydration' ? <li key={habit.id} data-id="element-3385" data-mp="wr1vsji8d2kt">
              {/* Hydration never completes on a plain tap — only by intake or explicit manual confirmation. */}
              <HabitItem habit={habit} completed={progress.completed.hydration} goal={hydration.completion === 'manual' ? t('hydration.manualDone') : hydration.targetMl ? t('hydration.goal', {
          target: formatLiters(hydration.targetMl, locale)
        }) : t('eng.first.hydrationNoTarget')} data-id="element-3386" data-mp="16mbx150t15xj" />
              {/* Weight is never required during onboarding — the user can add it later. */}
              {!hydration.targetMl && hydration.completion !== 'manual' && <p className="px-1 pt-1 text-[11.5px] leading-snug text-muted" data-id="element-3387" data-mp="1sey3v340vfif">
                  {t('eng.first.hydrationNoTargetHint')}
                </p>}
              {hydration.completion !== 'intake' && <button type="button" onClick={hydration.completion === 'manual' ? hydration.undoManual : hydration.markManual} className="flex min-h-[44px] items-center px-1 text-[12px] font-medium text-soft underline-offset-2 transition-colors duration-150 ease-brand hover:text-deep hover:underline" data-id="element-3388" data-mp="26ez1m9ozq6to">
                  {hydration.completion === 'manual' ? t('hydration.manualUndo') : t('hydration.manualMark')}
                </button>}
            </li> : <li key={habit.id} data-id="element-3389" data-mp="3lyzplrorgw9r">
              <HabitItem habit={habit} completed={progress.completed[habit.id]} onToggle={() => progress.toggleHabit(habit.id)} data-id="element-3390" data-mp="3lzrk0z6mgnyg" />
            </li>)}
      </ul>

      <h2 className="mt-3 text-[14px] font-bold text-deep" data-id="element-3391" data-mp="dhv6hgcq0j36">{t('eng.first.mood')}</h2>
      <div className="mt-2" data-id="element-3392" data-mp="8j22cejbq4fo">
        <MoodSelector value={progress.mood} onChange={progress.setMood} data-id="element-3393" data-mp="8jtm8s3k6dq4" />
      </div>

      <p className="mt-3 text-[11.5px] text-muted" data-id="element-3394" data-mp="2y83ykws4q53k">{t('eng.first.hint')}</p>
    </EntryLayout>;
}